import cx from 'classnames';
import Cookies from 'js-cookie';
import Link from 'next/link';
import { useRouter } from 'next/router';
import useTranslation from 'next-translate/useTranslation';
import React, { FunctionComponent, ReactNode } from 'react';

import useLinkProps from '../../../../hooks/use-link-props';
import saveUserCars from '../../../../services/user/save-cars';

import useModelVersionLink from './use-model-version-link';
import { useAtom } from 'jotai';
import { modelAtom, versionAtom } from '../../../../hooks/use-picker-selection';
import { selectVehicleStepAtom } from '../../../../state/vehicle';
import Button from '../../button';

type ListItemBrand = {
  id: number;
  slug: string;
  name: string;
  logo_url: string;
  is_top_brand: boolean;
  total_products: number;
};

type ListItemModel = {
  id: number;
  slug: string;
  name: string;
  have_versions: boolean;
};

type ListItemVersion = {
  id: number;
  slug: string;
  name: string;
};

type ListItem = {
  itemTag?: keyof JSX.IntrinsicElements;
  value?: ListItemBrand | ListItemModel | ListItemVersion;
  isPicker?: boolean;
  isAllDisabled?: boolean;
  customClickFunction?: boolean;
  displayList?: boolean;
  onClick?: () => void;
  children?: ReactNode;
};

const ListItem: FunctionComponent<ListItem> = ({
  itemTag: ItemTag = 'h4',
  value,
  isPicker = false,
  onClick,
  isAllDisabled = false,
  customClickFunction,
  children,
  displayList,
}) => {
  const { t } = useTranslation();
  const { locale = 'en' } = useRouter();
  const {
    slug,
    name,
    start_date,
    end_date,
    have_versions = false,
  } = value || {};

  const saveCar = () => {
    if (!have_versions) {
      saveUserCars({
        locale,
        car_identifier: slug,
        csrftoken: Cookies.get('csrftoken') || '',
      });
    }
  };

  const endDate = end_date || new Date().getFullYear();

  const linkProps = useModelVersionLink({ isPicker, have_versions, slug });

  return (
    <li className={cx()} onClick={saveCar}>
      <ItemTag>
        {isAllDisabled ? (
          <div className="text-neutral-50 cursor-default">
            {children || (
              <>
                <span className={cx('lg:text-sm', 'font-bold')}>{name} </span>
                <span className={cx('lg:ml-0', 'lg:block')}>
                  [{start_date}-{endDate}]
                </span>
              </>
            )}
          </div>
        ) : customClickFunction ? (
          <Button
            variant="DEFAULT"
            className={cx(
              'block',
              'relative',
              'p-4',
              'text-sm',
              'font-light',
              'leading-5 lg:leading-6',
              'focus:bg-brand-20',
              'hover:bg-brand-10',
              'focus:outline-none',
              'disabled:bg-neutral-10',
              'disabled:text-neutral-50',
              'disabled:cursor-default',
              'flex !min-h-12 !h-auto gap-4 items-center justify-between'
            )}
            type="button"
            onClick={onClick}
          >
            {children || (
              <>
                <span className={cx('lg:text-sm', 'font-bold text-left')}>
                  {name}
                </span>
                <span className={cx('lg:ml-0', 'lg:block')}>
                  [{start_date}-{endDate}]
                </span>
              </>
            )}
          </Button>
        ) : (
          <Link
            {...linkProps}
            shallow
            scroll
            className={cx(
              'block',
              'relative',
              'py-1',
              'px-4',
              'text-sm',
              'font-light',
              'leading-5 lg:leading-6',
              'focus:bg-brand-20',
              'hover:bg-brand-10',
              'focus:outline-none',
              'disabled:bg-neutral-10',
              'disabled:text-neutral-50',
              'disabled:cursor-default',
              'max-lg:flex max-lg:min-h-12 max-lg:gap-4 max-lg:items-center max-lg:justify-between'
            )}
            {...(onClick && { onClick: () => onClick(value) })}
          >
            {children || (
              <>
                <span className={cx('lg:text-sm', 'font-bold')}>{name} </span>
                <span className={cx('lg:ml-0', 'lg:block')}>
                  [{start_date}-{endDate}]
                </span>
              </>
            )}
          </Link>
        )}
      </ItemTag>
    </li>
  );
};

export default ListItem;
